import React, { useState, useEffect } from 'react';
import { X, BarChart2, TrendingUp, TrendingDown, Users, DollarSign, Printer } from 'lucide-react';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' },   { value: 4, label: 'April' },
  { value: 5, label: 'May' },     { value: 6, label: 'June' },
  { value: 7, label: 'July' },    { value: 8, label: 'August' },
  { value: 9, label: 'September'},{ value: 10, label: 'October' },
  { value: 11, label: 'November'},{ value: 12, label: 'December' }
];

const ArtSchoolReportModal = ({ academicYear, onClose, apiCall }) => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear]   = useState(now.getFullYear());
  const [report, setReport]               = useState(null);
  const [payouts, setPayouts]             = useState([]);
  const [loading, setLoading]             = useState(true);
  const [activeTab, setActiveTab]         = useState('summary');

  const load = async (month, year) => {
    setLoading(true);
    try {
      const [monthly, teacherPayouts] = await Promise.all([
        apiCall(`/artschool/reports/monthly?academicYear=${academicYear}&month=${month}&year=${year}`),
        apiCall(`/artschool/reports/teacher-payout?academicYear=${academicYear}&month=${month}&year=${year}`)
      ]);
      setReport(monthly);
      setPayouts(teacherPayouts);
    } catch {
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(selectedMonth, selectedYear);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [academicYear]);

  const handleMonthChange = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    load(month, year);
  };

  const handlePrint = () => {
    if (!report) return;
    const monthLabel = MONTHS.find(m => m.value === selectedMonth)?.label;
    const fmtDate    = `${monthLabel} ${selectedYear}`;
    const now        = new Date().toLocaleDateString('en-US', { year:'numeric', month:'long', day:'numeric' });

    const openPrint = (title, body) => {
      const w = window.open('', '', 'width=900,height=700');
      w.document.write(`
        <html>
          <head>
            <meta charset="UTF-8">
            <title>${title}</title>
            <style>
              * { margin:0; padding:0; box-sizing:border-box; }
              body { font-family: Arial, sans-serif; padding: 24px; color: #1f2937; }
              @media print { body { padding: 10px; } @page { size: A4; margin: 1cm; } }
            </style>
          </head>
          <body>
            <!-- Header -->
            <div style="text-align:center;margin-bottom:24px;padding-bottom:16px;border-bottom:3px solid #4f46e5;">
              <h1 style="font-size:26px;font-weight:700;color:#1f2937;margin-bottom:4px;">Collège des Apôtres</h1>
              <h2 style="font-size:16px;font-weight:600;color:#4f46e5;margin-bottom:4px;">Art School — ${title}</h2>
              <p style="font-size:13px;color:#6b7280;">Period: ${fmtDate} &nbsp;·&nbsp; Academic Year: ${academicYear}</p>
              <p style="font-size:11px;color:#9ca3af;margin-top:4px;">Generated: ${now}</p>
            </div>
            ${body}
            <!-- Footer -->
            <div style="margin-top:40px;padding-top:12px;border-top:2px solid #e5e7eb;display:flex;justify-content:space-between;align-items:flex-end;">
              <div style="font-size:11px;color:#9ca3af;">Collège des Apôtres · Art School Program · ${academicYear}</div>
              <div style="text-align:right;">
                <div style="border-top:1px solid #374151;padding-top:6px;margin-top:36px;font-size:11px;font-weight:600;color:#374151;">Authorized Signature</div>
              </div>
            </div>
          </body>
        </html>
      `);
      w.document.close();
      w.focus();
      setTimeout(() => w.print(), 300);
    };

    // ── SUMMARY tab ────────────────────────────────────────────
    if (activeTab === 'summary') {
      const byCategory = {};
      report.classBreakdown.forEach(row => {
        if (!byCategory[row.CategoryName]) byCategory[row.CategoryName] = [];
        byCategory[row.CategoryName].push(row);
      });

      const catRows = Object.entries(byCategory).map(([cat, classes]) => {
        const catRevenue = classes.reduce((s, r) => s + parseFloat(r.MonthlyRevenue || 0), 0);
        const catStudents = classes.reduce((s, r) => s + parseInt(r.ActiveStudents || 0), 0);
        return `
          <tr style="border-bottom:1px solid #e5e7eb;">
            <td style="padding:8px 10px;font-size:13px;font-weight:600;">${cat}</td>
            <td style="padding:8px 10px;font-size:13px;text-align:center;">${catStudents}</td>
            <td style="padding:8px 10px;font-size:13px;text-align:right;font-weight:600;color:#1d4ed8;">$${catRevenue.toFixed(2)}</td>
          </tr>
        `;
      }).join('');

      const body = `
        <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:12px;margin-bottom:24px;">
          <div style="border:2px solid #3b82f6;border-radius:8px;padding:12px;text-align:center;background:#eff6ff;">
            <div style="font-size:11px;color:#6b7280;margin-bottom:4px;">Total Students</div>
            <div style="font-size:22px;font-weight:700;color:#1d4ed8;">${report.totals.TotalStudents}</div>
          </div>
          <div style="border:2px solid #22c55e;border-radius:8px;padding:12px;text-align:center;background:#f0fdf4;">
            <div style="font-size:11px;color:#6b7280;margin-bottom:4px;">Monthly Revenue</div>
            <div style="font-size:22px;font-weight:700;color:#15803d;">$${parseFloat(report.totals.TotalRevenue).toFixed(2)}</div>
          </div>
          <div style="border:2px solid #f97316;border-radius:8px;padding:12px;text-align:center;background:#fff7ed;">
            <div style="font-size:11px;color:#6b7280;margin-bottom:4px;">Teacher Payouts</div>
            <div style="font-size:22px;font-weight:700;color:#c2410c;">$${parseFloat(report.totals.TotalPayout).toFixed(2)}</div>
          </div>
          <div style="border:2px solid #8b5cf6;border-radius:8px;padding:12px;text-align:center;background:#f5f3ff;">
            <div style="font-size:11px;color:#6b7280;margin-bottom:4px;">Net Income</div>
            <div style="font-size:22px;font-weight:700;color:#6d28d9;">$${parseFloat(report.totals.NetIncome).toFixed(2)}</div>
          </div>
        </div>
        <h3 style="font-size:14px;font-weight:700;margin:0 0 10px 0;color:#1f2937;">Revenue by Category</h3>
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f9fafb;">
              <th style="padding:8px 10px;text-align:left;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Category</th>
              <th style="padding:8px 10px;text-align:center;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Students</th>
              <th style="padding:8px 10px;text-align:right;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Revenue</th>
            </tr>
          </thead>
          <tbody>${catRows}</tbody>
        </table>
      `;
      openPrint('Monthly Summary', body);

    // ── CLASSES tab ────────────────────────────────────────────
    } else if (activeTab === 'classes') {
      const rows = report.classBreakdown.map(row => `
        <tr style="border-bottom:1px solid #e5e7eb;">
          <td style="padding:8px 10px;font-size:13px;">${row.CategoryName}</td>
          <td style="padding:8px 10px;font-size:13px;font-weight:600;">${row.ClassName}</td>
          <td style="padding:8px 10px;font-size:12px;color:#6b7280;">${row.ClassType}</td>
          <td style="padding:8px 10px;font-size:12px;color:#6b7280;">${row.DayOfWeek || '—'}</td>
          <td style="padding:8px 10px;font-size:13px;text-align:center;">${row.SessionsInMonth || 4}</td>
          <td style="padding:8px 10px;font-size:13px;text-align:center;">${row.ActiveStudents}</td>
          <td style="padding:8px 10px;font-size:13px;text-align:right;font-weight:600;color:#1d4ed8;">$${parseFloat(row.MonthlyRevenue || 0).toFixed(2)}</td>
          <td style="padding:8px 10px;font-size:13px;text-align:right;font-weight:600;color:#dc2626;">$${parseFloat(row.MonthlyTeacherPayout || 0).toFixed(2)}</td>
        </tr>
      `).join('');

      const body = `
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#f9fafb;">
              <th style="padding:8px 10px;text-align:left;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Category</th>
              <th style="padding:8px 10px;text-align:left;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Class</th>
              <th style="padding:8px 10px;text-align:left;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Type</th>
              <th style="padding:8px 10px;text-align:left;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Day</th>
              <th style="padding:8px 10px;text-align:center;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Sessions</th>
              <th style="padding:8px 10px;text-align:center;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Students</th>
              <th style="padding:8px 10px;text-align:right;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Revenue</th>
              <th style="padding:8px 10px;text-align:right;font-size:11px;color:#6b7280;border-bottom:2px solid #e5e7eb;">Payout</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr style="background:#f3f4f6;font-weight:700;">
              <td colspan="6" style="padding:10px;text-align:right;font-size:13px;border-top:2px solid #d1d5db;">Totals:</td>
              <td style="padding:10px;text-align:right;font-size:13px;color:#1d4ed8;border-top:2px solid #d1d5db;">$${parseFloat(report.totals.TotalRevenue).toFixed(2)}</td>
              <td style="padding:10px;text-align:right;font-size:13px;color:#dc2626;border-top:2px solid #d1d5db;">$${parseFloat(report.totals.TotalPayout).toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      `;
      openPrint('Class Breakdown', body);

    // ── TEACHERS tab ───────────────────────────────────────────
    } else if (activeTab === 'teachers') {
      const teacherSections = payouts.map(teacher => `
        <div style="margin-bottom:24px;page-break-inside:avoid;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;">
          <div style="display:flex;justify-content:space-between;align-items:center;background:#f9fafb;padding:12px 16px;border-bottom:1px solid #e5e7eb;">
            <div>
              <div style="font-size:15px;font-weight:700;color:#1f2937;">${teacher.teacherName}</div>
              <div style="font-size:12px;color:#6b7280;margin-top:2px;">
                ${teacher.teacherPhone || ''} ${teacher.teacherEmail ? '· ' + teacher.teacherEmail : ''}
              </div>
            </div>
            <div style="text-align:right;">
              <div style="font-size:11px;color:#6b7280;">${teacher.totalStudents} students</div>
              <div style="font-size:20px;font-weight:700;color:#ea580c;">$${teacher.totalPayout.toFixed(2)}</div>
            </div>
          </div>
          <table style="width:100%;border-collapse:collapse;">
            <thead>
              <tr style="background:#fffbeb;">
                <th style="padding:7px 12px;text-align:left;font-size:11px;color:#6b7280;border-bottom:1px solid #e5e7eb;">Class</th>
                <th style="padding:7px 12px;text-align:left;font-size:11px;color:#6b7280;border-bottom:1px solid #e5e7eb;">Type</th>
                <th style="padding:7px 12px;text-align:left;font-size:11px;color:#6b7280;border-bottom:1px solid #e5e7eb;">Day</th>
                <th style="padding:7px 12px;text-align:center;font-size:11px;color:#6b7280;border-bottom:1px solid #e5e7eb;">Sessions</th>
                <th style="padding:7px 12px;text-align:center;font-size:11px;color:#6b7280;border-bottom:1px solid #e5e7eb;">Students</th>
                <th style="padding:7px 12px;text-align:right;font-size:11px;color:#6b7280;border-bottom:1px solid #e5e7eb;">Payout</th>
              </tr>
            </thead>
            <tbody>
              ${teacher.classes.map(cls => `
                <tr style="border-bottom:1px solid #f3f4f6;">
                  <td style="padding:8px 12px;font-size:13px;">${cls.ClassName}</td>
                  <td style="padding:8px 12px;font-size:12px;color:#6b7280;">${cls.ClassType}</td>
                  <td style="padding:8px 12px;font-size:12px;color:#6b7280;">${cls.DayOfWeek || '—'}</td>
                  <td style="padding:8px 12px;font-size:13px;text-align:center;">${cls.SessionsInMonth || 4}</td>
                  <td style="padding:8px 12px;font-size:13px;text-align:center;">${cls.ActiveStudents}</td>
                  <td style="padding:8px 12px;font-size:13px;text-align:right;font-weight:600;color:#ea580c;">$${parseFloat(cls.MonthlyTeacherPayout || 0).toFixed(2)}</td>
                </tr>
              `).join('')}
            </tbody>
            <tfoot>
              <tr style="background:#fff7ed;">
                <td colspan="5" style="padding:8px 12px;text-align:right;font-size:13px;font-weight:600;border-top:2px solid #fed7aa;">Total Payout:</td>
                <td style="padding:8px 12px;text-align:right;font-size:14px;font-weight:700;color:#ea580c;border-top:2px solid #fed7aa;">$${teacher.totalPayout.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      `).join('');

      const grandPayoutTotal = payouts.reduce((s, t) => s + t.totalPayout, 0);

      const body = `
        ${teacherSections}
        <div style="margin-top:16px;padding:12px 16px;background:#fff7ed;border:2px solid #fed7aa;border-radius:8px;display:flex;justify-content:space-between;align-items:center;">
          <span style="font-size:15px;font-weight:700;color:#92400e;">Total Payout — All Teachers:</span>
          <span style="font-size:20px;font-weight:700;color:#ea580c;">$${grandPayoutTotal.toFixed(2)}</span>
        </div>
      `;
      openPrint('Teacher Payouts', body);

    // ── OUTSTANDING tab ────────────────────────────────────────
    } else if (activeTab === 'outstanding') {
      const rows = (report.outstandingFamilies || []).map(f => `
        <tr style="border-bottom:1px solid #e5e7eb;">
          <td style="padding:8px 10px;font-size:13px;">${f.FamilyID}</td>
          <td style="padding:8px 10px;font-size:13px;">${f.FamilyName}</td>
          <td style="padding:8px 10px;font-size:12px;color:#6b7280;">${f.PrimaryPhone || '—'}</td>
          <td style="padding:8px 10px;font-size:13px;text-align:right;font-weight:600;color:#dc2626;">$${parseFloat(f.Balance || 0).toFixed(2)}</td>
        </tr>
      `).join('');

      const totalOut = (report.outstandingFamilies || []).reduce((s, f) => s + parseFloat(f.Balance || 0), 0);

      const body = `
        <table style="width:100%;border-collapse:collapse;">
          <thead>
            <tr style="background:#fef2f2;">
              <th style="padding:8px 10px;text-align:left;font-size:11px;color:#6b7280;border-bottom:2px solid #fecaca;">Family ID</th>
              <th style="padding:8px 10px;text-align:left;font-size:11px;color:#6b7280;border-bottom:2px solid #fecaca;">Family Name</th>
              <th style="padding:8px 10px;text-align:left;font-size:11px;color:#6b7280;border-bottom:2px solid #fecaca;">Phone</th>
              <th style="padding:8px 10px;text-align:right;font-size:11px;color:#6b7280;border-bottom:2px solid #fecaca;">Balance Due</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
          <tfoot>
            <tr style="background:#fef2f2;">
              <td colspan="3" style="padding:10px;text-align:right;font-size:13px;font-weight:600;border-top:2px solid #fecaca;">Total Outstanding:</td>
              <td style="padding:10px;text-align:right;font-size:14px;font-weight:700;color:#dc2626;border-top:2px solid #fecaca;">$${totalOut.toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      `;
      openPrint('Outstanding Balances', body);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BarChart2 size={20} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-800">Monthly Report</h2>
              <p className="text-sm text-gray-500">Art School · {academicYear}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Month picker */}
            <select
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
              value={selectedMonth}
              onChange={e => handleMonthChange(parseInt(e.target.value), selectedYear)}
            >
              {MONTHS.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <input
              type="number"
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm w-20 focus:ring-2 focus:ring-blue-500"
              value={selectedYear}
              onChange={e => handleMonthChange(selectedMonth, parseInt(e.target.value))}
              min="2020" max="2030"
            />
            <button
              onClick={handlePrint}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Printer size={15} /> Print
            </button>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b px-5">
          {['summary', 'classes', 'teachers', 'outstanding'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors capitalize
                ${activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'outstanding' ? 'Outstanding' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center h-48 text-gray-400">
              Loading report...
            </div>
          ) : !report ? (
            <div className="flex items-center justify-center h-48 text-gray-400">
              No data available for {academicYear}
            </div>
          ) : (
            <>
              {/* Summary Tab */}
              {activeTab === 'summary' && (
                <div className="space-y-6">
                  <div className="text-sm font-medium text-gray-500 pb-1 border-b">
                    {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatCard
                      label="Total Students"
                      value={report.totals?.TotalStudents || 0}
                      icon={<Users size={20} className="text-blue-600" />}
                      color="blue"
                    />
                    <StatCard
                      label="Monthly Revenue"
                      value={`$${parseFloat(report.totals?.TotalRevenue || 0).toFixed(2)}`}
                      icon={<TrendingUp size={20} className="text-green-600" />}
                      color="green"
                    />
                    <StatCard
                      label="Teacher Payouts"
                      value={`$${parseFloat(report.totals?.TotalPayout || 0).toFixed(2)}`}
                      icon={<TrendingDown size={20} className="text-orange-600" />}
                      color="orange"
                    />
                    <StatCard
                      label="Net Income"
                      value={`$${parseFloat(report.totals?.NetIncome || 0).toFixed(2)}`}
                      icon={<DollarSign size={20} className="text-indigo-600" />}
                      color="indigo"
                    />
                  </div>

                  {/* Category breakdown */}
                  {report.classBreakdown && report.classBreakdown.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-700 mb-3">Revenue by Category</h3>
                      <div className="space-y-2">
                        {Object.entries(
                          report.classBreakdown.reduce((acc, row) => {
                            if (!acc[row.CategoryName]) acc[row.CategoryName] = { revenue: 0, students: 0 };
                            acc[row.CategoryName].revenue  += parseFloat(row.MonthlyRevenue || 0);
                            acc[row.CategoryName].students += parseInt(row.ActiveStudents || 0);
                            return acc;
                          }, {})
                        ).map(([cat, data]) => (
                          <div key={cat} className="flex items-center gap-3">
                            <div className="w-28 text-sm text-gray-600 truncate">{cat}</div>
                            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-indigo-500 h-2 rounded-full"
                                style={{
                                  width: `${Math.min(100, (data.revenue / (report.totals?.TotalRevenue || 1)) * 100)}%`
                                }}
                              />
                            </div>
                            <div className="text-sm font-medium text-gray-700 w-20 text-right">
                              ${data.revenue.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-400 w-16 text-right">
                              {data.students} student{data.students !== 1 ? 's' : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Classes Tab */}
              {activeTab === 'classes' && (
                <div>
                  {report.classBreakdown?.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="px-3 py-2 font-semibold text-gray-600 rounded-l">Class</th>
                          <th className="px-3 py-2 font-semibold text-gray-600">Category</th>
                          <th className="px-3 py-2 font-semibold text-gray-600">Type</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-right">Students</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-right">Revenue</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-right rounded-r">Payout</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {report.classBreakdown.map(row => (
                          <tr key={`${row.ClassID}-${row.ChargeMonth}-${row.ChargeYear}`} className="hover:bg-gray-50">
                            <td className="px-3 py-2.5 font-medium text-gray-800">{row.ClassName}</td>
                            <td className="px-3 py-2.5 text-gray-600">{row.CategoryName}</td>
                            <td className="px-3 py-2.5">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                                ${row.ClassType === 'Private'    ? 'bg-purple-100 text-purple-700'
                                : row.ClassType === 'SmallGroup' ? 'bg-blue-100 text-blue-700'
                                : 'bg-green-100 text-green-700'}`}>
                                {row.ClassType}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-right text-gray-700">{row.ActiveStudents}</td>
                            <td className="px-3 py-2.5 text-right text-green-700 font-medium">
                              ${parseFloat(row.MonthlyRevenue || 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-orange-600 font-medium">
                              ${parseFloat(row.MonthlyTeacherPayout || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 font-semibold border-t-2">
                          <td className="px-3 py-2" colSpan={3}>Total</td>
                          <td className="px-3 py-2 text-right">{report.totals?.TotalStudents}</td>
                          <td className="px-3 py-2 text-right text-green-700">
                            ${parseFloat(report.totals?.TotalRevenue || 0).toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-right text-orange-600">
                            ${parseFloat(report.totals?.TotalPayout || 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : (
                    <p className="text-center text-gray-400 py-12">No active classes for {academicYear}</p>
                  )}
                </div>
              )}

              {/* Teachers Tab */}
              {activeTab === 'teachers' && (
                <div className="space-y-4">
                  {payouts.length > 0 ? payouts.map(teacher => (
                    <div key={teacher.teacherID} className="border rounded-xl overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 bg-gray-50">
                        <div>
                          <div className="font-semibold text-gray-800">{teacher.teacherName}</div>
                          <div className="text-xs text-gray-500 flex gap-3 mt-0.5">
                            {teacher.teacherPhone && <span>{teacher.teacherPhone}</span>}
                            {teacher.teacherEmail && <span>{teacher.teacherEmail}</span>}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-gray-500">{teacher.totalStudents} students</div>
                          <div className="font-bold text-orange-600 text-lg">
                            ${teacher.totalPayout.toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-t text-xs text-gray-500">
                            <th className="px-4 py-1.5 text-left font-medium">Class</th>
                            <th className="px-4 py-1.5 text-left font-medium">Type</th>
                            <th className="px-4 py-1.5 text-left font-medium">Day</th>
                            <th className="px-4 py-1.5 text-right font-medium">Sessions</th>
                            <th className="px-4 py-1.5 text-right font-medium">Students</th>
                            <th className="px-4 py-1.5 text-right font-medium">Payout</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {teacher.classes.map(cls => (
                            <tr key={`${cls.ClassID}-${cls.ChargeMonth}-${cls.ChargeYear}`}>
                              <td className="px-4 py-2">{cls.ClassName}</td>
                              <td className="px-4 py-2 text-gray-500">{cls.ClassType}</td>
                              <td className="px-4 py-2 text-gray-500">{cls.DayOfWeek || '—'}</td>
                              <td className="px-4 py-2 text-right">{cls.SessionsInMonth || 4}</td>
                              <td className="px-4 py-2 text-right">{cls.ActiveStudents}</td>
                              <td className="px-4 py-2 text-right font-medium text-orange-600">
                                ${parseFloat(cls.MonthlyTeacherPayout || 0).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )) : (
                    <p className="text-center text-gray-400 py-12">No teacher payout data for {academicYear}</p>
                  )}
                </div>
              )}

              {/* Outstanding Tab */}
              {activeTab === 'outstanding' && (
                <div>
                  {report.outstandingFamilies?.length > 0 ? (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-gray-50 text-left">
                          <th className="px-3 py-2 font-semibold text-gray-600 rounded-l">Family</th>
                          <th className="px-3 py-2 font-semibold text-gray-600">Phone</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-right">Charged</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-right">Paid</th>
                          <th className="px-3 py-2 font-semibold text-gray-600 text-right rounded-r">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {report.outstandingFamilies.map((f, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-3 py-2.5 font-medium text-gray-800">{f.FamilyName}</td>
                            <td className="px-3 py-2.5 text-gray-500">{f.PrimaryPhone || '—'}</td>
                            <td className="px-3 py-2.5 text-right text-gray-600">
                              ${parseFloat(f.TotalCharges || 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-2.5 text-right text-green-600">
                              ${parseFloat(f.TotalPayments || 0).toFixed(2)}
                            </td>
                            <td className="px-3 py-2.5 text-right font-bold text-red-600">
                              ${parseFloat(f.Balance || 0).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400 gap-2">
                      <DollarSign size={32} className="text-green-300" />
                      <p>No outstanding balances — all families are settled!</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, icon, color }) => {
  const colors = {
    blue:   'bg-blue-50   border-blue-100',
    green:  'bg-green-50  border-green-100',
    orange: 'bg-orange-50 border-orange-100',
    indigo: 'bg-indigo-50 border-indigo-100'
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="flex items-center justify-between mb-2">
        {icon}
      </div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
};

export default ArtSchoolReportModal;
